import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { EventCategory } from '@models/event-category.model';
import { BettingService } from '@services/mts/betting.service';
import { HttpClient } from '@angular/common/http';
import { TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SportEnum } from '@shared/enums/sport.enum';
import { BetManagerService } from '@services/mts/bet-manager.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { OddManagerService } from '@services/uof/odd-manager.service';
import { SubjectsService } from '@app/services/subjects.service';
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    imports: [TranslocoModule, CommonModule, RouterModule, FormsModule],
})
export class SidebarComponent implements OnInit {
    protected readonly SportEnum = SportEnum;
    public favouriteCompetitions = toSignal(this.oddManagerService.selectedCompetitions$);
    eventCategories: EventCategory = {} as EventCategory;
    sports: any[] = [];
    popularLeagues: any;
    openSidebar: boolean = true;
    activeSport: string = '';
    isDropdownOpen: { [key: string]: boolean } = { sports: true, favourite: true };
    mobileTab: boolean = false;
    sportTab: boolean = false;

    isSubDropdownOpen: { [key: string]: boolean } = {};

    sidePanelData = this.betManagerService.sidePanel;

    private subjectService = inject(SubjectsService);
    constructor(
        private bettingService: BettingService,
        private router: Router,
        private route: ActivatedRoute,
        private betManagerService: BetManagerService,
        private oddManagerService: OddManagerService,
    ) {
        this.checkScreenWidth();
        window.addEventListener('resize', () => this.checkScreenWidth());
        this.updateTopTabsFromUrl(this.router.url);
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.updateTopTabsFromUrl(event.urlAfterRedirects || event.url);
            }
        });
        effect(() => {
            if (this.sidePanelData()) {
                this.eventCategories = this.sidePanelData();
                this.getFavouriteCompetitions();
            }
        });
    }

    ngOnInit() {
        // Get the active sport sidebar from behaviour subject and if not from localstorage
        this.subjectService.activeSportSidebar$.subscribe((activeSport) => {
            this.activeSport = activeSport;
        });
        const activeSport = localStorage.getItem('activeSportSidebar');
        if (activeSport && !this.activeSport) {
            this.activeSport = activeSport;
        }
    }

    checkScreenWidth() {
        const screenWidth = window.innerWidth;
        if (screenWidth > 1300) {
            this.isDropdownOpen = { sports: true, favourite: true };
        } else {
            this.isDropdownOpen = { sports: true, favourite: false };
        }
    }

    toggleDropdown(menuType: string) {
        Object.keys(this.isDropdownOpen).forEach((key) => {
            if (key !== menuType && key !== 'sports' && key !== 'favourite') {
                this.isDropdownOpen[key] = false;
                this.isSubDropdownOpen[key] = false;
            }
        });

        this.isDropdownOpen[menuType] = !this.isDropdownOpen[menuType];
    }

    toggleTemplate(menuType: string) {
        this.isSubDropdownOpen[menuType] = !this.isSubDropdownOpen[menuType];
    }

    isAnyCheckboxChecked(): boolean {
        return this.favouriteCompetitions().some((comp) => comp.isChecked);
    }

    getFavouriteCompetitions(): void {
        if (
            this.eventCategories.favouriteCompetitions &&
            Array.isArray(this.eventCategories.favouriteCompetitions)
        ) {
            const existingFavorites = this.favouriteCompetitions();
            const newFavorites = this.eventCategories.favouriteCompetitions.filter(
                (newFav) => !existingFavorites.some((existingFav) => existingFav.id === newFav.id),
            );
            this.favouriteCompetitions().push(...newFavorites);
            this.checkPreSelectedCompetitions();
        }
        if (this.eventCategories.sports && Object.keys(this.eventCategories.sports).length) {
            Object.keys(this.eventCategories.sports).forEach((key) =>
                this.sports.push(this.eventCategories.sports[key]),
            );

            const validCategories = ['Soccer', 'Basketball', 'Tennis', 'Volleyball'];

            this.popularLeagues = this.sports
                .filter(
                    (sport) => sport.mainCompetitions && validCategories.includes(sport.sportName),
                )
                .flatMap((sport) =>
                    sport.mainCompetitions.map((competition) => ({
                        competition: competition,
                        sportName: sport.sportName,
                    })),
                );

            this.bettingService.updatePopularLeagues(this.popularLeagues);
        }
    }

    getSportTemplate(sportName: string): any[] {
        const sport = this.sports.find((s) => s.sportName === sportName);
        if (sport) {
            return sport.templates;
        }
        return [];
    }

    checkPreSelectedCompetitions() {
        const tournamentId = this.route.snapshot.paramMap.get('tournamentId');
        if (tournamentId) {
            const tournamentIds = tournamentId.split(',');
            this.favouriteCompetitions().forEach((competition) => {
                tournamentIds.forEach((tournamentId) => {
                    if (competition.id === tournamentId) {
                        competition.isChecked = true;
                    }
                });
            });
        }
    }

    routeToTournament(sportId: string, eventId: string): void {
        const sportName = SportEnum[sportId];
        this.router.navigate(['/sport', sportName.replace(' ', ''), 'tournament', eventId]);
    }

    activeSportName(sportName: string, sportId: number) {
        // Setting the active sport
        this.activeSport = sportName;
        localStorage.setItem('activeSportSidebar', sportName);
        this.subjectService.activeSportSidebar$.next(sportName);
        this.router.navigate(['/sport', SportEnum[sportId]]);
    }

    setActiveSport(sportName: string) {
        this.activeSport = sportName;
    }

    formatName(name: string): string {
        return name.toLowerCase().replace(/\s+/g, '-');
    }

    trackByFactory(key: string) {
        return (index: number, item: any): any => {
            return item[key] + index + new Date().toISOString();
        };
    }

    removeSportColons(sportId: string | undefined): string {
        return sportId?.replace(/:/g, '') || '';
    }

    routeToTournaments(competitions: any): void {
        let ids: string[] = [];
        competitions.forEach((competition) => {
            if (competition.isChecked) {
                ids.push(competition.id);
            }
        });
        const id = ids.join(',');
        this.router.navigate(['sport', SportEnum[competitions[0].sportId], 'tournament', id]);
    }

    getSportNameLocalized(name: string) {
        let sportName = name;
        if (name.includes(' ')) {
            const splittedName = sportName.split(' ');
            sportName = splittedName.join('_');
        }
        return 'sidebar.sports.' + sportName.toLowerCase();
    }

    setTopTab(tab: 'mobile' | 'e-sports') {
        this.mobileTab = tab === 'mobile';
        this.sportTab = tab === 'e-sports';
    }

    private updateTopTabsFromUrl(url: string) {
        if (!url) {
            this.mobileTab = false;
            this.sportTab = false;
            return;
        }
        const normalized = url.split('?')[0].split('#')[0];
        this.mobileTab = normalized.startsWith('/mobile');
        this.sportTab = normalized.startsWith('/e-sports');
    }
}
